/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import {
  MessageSquare, Smartphone, ArrowRight, ExternalLink,
  ShieldCheck, Building, LogIn, LogOut, User, Check, Crown
} from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { Vendor } from '../types';

interface LandingPageProps {
  session: Session | null;
  currentVendor: Vendor | null;
  onOpenDashboard: () => void;
  onOpenCatalog: (businessName: string) => void;
  onOpenAuth: (mode: 'login' | 'register') => void;
  onSignOut: () => void;
}

export default function LandingPage({
  session,
  currentVendor,
  onOpenDashboard,
  onOpenCatalog,
  onOpenAuth,
  onSignOut,
}: LandingPageProps) {
  return (
    <div className="min-h-screen bg-transparent text-slate-900 font-sans selection:bg-orange-100">

      {/* Top accent bar */}
      <div className="w-full h-1.5 bg-gradient-to-r from-orange-500 via-amber-400 to-emerald-400" />

      {/* Nav */}
      <nav className="max-w-5xl mx-auto px-4 pt-5 flex items-center justify-between gap-2">
        <span className="font-display font-black text-lg text-slate-900 tracking-tight shrink-0">
          Vendor<span className="text-orange-500">Pulse</span>
        </span>
        <div className="flex items-center gap-2">
          {session ? (
            <>
              <button
                onClick={onOpenDashboard}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold tracking-wide transition-all flex items-center gap-1.5 whitespace-nowrap"
              >
                <Building className="w-3.5 h-3.5 shrink-0" />
                <span>Dashboard</span>
              </button>
              <button
                onClick={onSignOut}
                className="px-3 py-1.5 border border-red-200 bg-red-50 hover:bg-red-100 rounded-xl text-xs font-semibold text-red-600 transition-all flex items-center gap-1.5 whitespace-nowrap"
              >
                <LogOut className="w-3.5 h-3.5 shrink-0" />
                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onOpenAuth('login')}
                className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-semibold text-slate-700 transition-all flex items-center gap-1 whitespace-nowrap"
              >
                <LogIn className="w-3.5 h-3.5 shrink-0" />
                <span>Sign in</span>
              </button>
              <button
                onClick={() => onOpenAuth('register')}
                className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-semibold whitespace-nowrap transition-all shadow-md shadow-orange-500/20"
              >
                Get Started
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 pt-14 pb-10 text-center max-w-4xl mx-auto z-10">
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-950 py-1 max-w-3xl mx-auto leading-tight">
          Visual Catalogues,{' '}
          <span className="text-orange-600 underline decoration-emerald-400/80 decoration-wavy py-1">
            WhatsApp
          </span>{' '}
          Checkout.
        </h1>

        <p className="mt-5 text-slate-500 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          Turn your Instagram products into a shareable catalog link.
          Buyers browse and order directly into WhatsApp — no gateways, no commissions.
        </p>

        {/* CTA buttons — only shown when logged out */}
        {!session && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <button
              onClick={() => onOpenAuth('register')}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm rounded-2xl transition-all shadow-lg shadow-orange-500/25 active:scale-95"
            >
              Create Your Catalog Free
            </button>
            <button
              onClick={() => onOpenAuth('login')}
              className="px-6 py-3 border border-slate-200 hover:bg-white text-slate-700 font-semibold text-sm rounded-2xl transition-all"
            >
              Sign in to Dashboard
            </button>
          </div>
        )}

        {/* How it works — 3 steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-14 max-w-3xl mx-auto text-left">
          <div className="bg-white/40 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-lg">
            <div className="w-8 h-8 bg-orange-500 text-white rounded-xl flex items-center justify-center mb-3 text-sm font-black">1</div>
            <h3 className="font-display font-bold text-slate-950 text-sm mb-1.5">Register your boutique</h3>
            <p className="text-slate-500 text-xs leading-normal">Takes 2 minutes. Get a permanent catalog link like <span className="font-mono text-orange-500">vendorpulse/yourbusiness</span></p>
          </div>

          <div className="bg-white/40 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-lg">
            <div className="w-8 h-8 bg-orange-500 text-white rounded-xl flex items-center justify-center mb-3 text-sm font-black">2</div>
            <h3 className="font-display font-bold text-slate-950 text-sm mb-1.5">Upload your products</h3>
            <p className="text-slate-500 text-xs leading-normal">Add photos, prices and stock from your phone. Updates go live instantly.</p>
          </div>

          <div className="bg-white/40 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-lg">
            <div className="w-8 h-8 bg-emerald-600 text-white rounded-xl flex items-center justify-center mb-3">
              <MessageSquare className="w-4 h-4" />
            </div>
            <h3 className="font-display font-bold text-slate-950 text-sm mb-1.5">Buyers order via WhatsApp</h3>
            <p className="text-slate-500 text-xs leading-normal">One tap sends a pre-filled order message straight to your WhatsApp. No checkout form.</p>
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-16">
        <div className="text-center mb-8">
          <h2 className="font-display font-bold text-2xl text-slate-900">Simple, honest pricing</h2>
          <p className="text-slate-500 text-sm mt-2">Start free. Upgrade when your catalog grows.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Free tier */}
          <div className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold text-slate-900 text-lg">Free</span>
              <span className="text-2xl font-black text-slate-900">₵0</span>
            </div>
            <p className="text-xs text-slate-500 mb-5">Everything you need to get started.</p>
            <ul className="space-y-2.5 mb-6">
              {[
                'Catalog link for your Instagram bio',
                'Up to 4 products',
                'Photo uploads',
                'WhatsApp order button',
                'Stock management',
              ].map(f => (
                <li key={f} className="flex items-start gap-2 text-xs text-slate-700">
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => onOpenAuth('register')}
              className="w-full py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl transition-all"
            >
              Get started free
            </button>
          </div>

          {/* Premium tier */}
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl relative overflow-hidden">
            {/* Glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/20 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-center justify-between mb-1 relative z-10">
              <div className="flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="font-display font-bold text-white text-lg">Premium</span>
              </div>
              <div className="text-right">
                <span className="text-2xl font-black text-white">₵25</span>
                <span className="text-slate-400 text-xs">/mo</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-5 relative z-10">Or ₵200/year — save 2 months.</p>
            <ul className="space-y-2.5 mb-6 relative z-10">
              {[
                'Everything in Free',
                'Unlimited products',
                'Priority WhatsApp support',
                'More features coming soon',
              ].map(f => (
                <li key={f} className="flex items-start gap-2 text-xs text-slate-300">
                  <Check className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => onOpenAuth('register')}
              className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-xs rounded-xl transition-all shadow-lg shadow-orange-500/30 relative z-10 active:scale-95"
            >
              Start free, upgrade anytime
            </button>
          </div>
        </div>
      </section>

      {/* My Boutique (logged in) OR sign-up/sign-in cards (logged out) */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-24">
        {session && currentVendor ? (
          <div className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-6 shadow-xl max-w-2xl mx-auto">
            <div className="flex items-center gap-2 mb-5">
              <Building className="w-5 h-5 text-orange-500" />
              <h2 className="font-display font-semibold text-base text-slate-900">Your Boutique</h2>
            </div>
            <div className="p-4 bg-white/65 backdrop-blur-xs border border-white rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-display font-semibold text-slate-900 capitalize text-base">
                    {currentVendor.business_name}
                  </span>
                  {currentVendor.subscription_status === 'premium' && (
                    <ShieldCheck className="w-4 h-4 text-emerald-500 fill-emerald-100 shrink-0" title="Premium" />
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{currentVendor.email}</p>
                <p className="text-[10px] font-mono text-slate-400 mt-0.5">+{currentVendor.whatsapp_number}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => onOpenCatalog(currentVendor.business_name)}
                  className="p-1.5 px-3 rounded-xl border border-slate-200 bg-white/80 hover:bg-white text-[11px] font-bold text-slate-600 transition-all flex items-center gap-1"
                >
                  <span>View Catalog</span>
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                </button>
                <button
                  onClick={onOpenDashboard}
                  className="p-1.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-[11px] font-bold text-white transition-all flex items-center gap-1"
                >
                  <span>Dashboard</span>
                  <ArrowRight className="w-3 h-3 text-orange-400" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div
              onClick={() => onOpenAuth('register')}
              className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-6 shadow-xl cursor-pointer hover:border-orange-400/50 hover:bg-white/60 transition-all group"
            >
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-orange-500/20">
                <User className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display font-bold text-slate-900 text-base mb-1">New vendor?</h3>
              <p className="text-slate-500 text-xs leading-relaxed mb-4">
                Create your free catalog in under 2 minutes. No card needed.
              </p>
              <span className="text-xs font-bold text-orange-500 group-hover:underline flex items-center gap-1">
                Create catalog free <ArrowRight className="w-3 h-3" />
              </span>
            </div>

            <div
              onClick={() => onOpenAuth('login')}
              className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-6 shadow-xl cursor-pointer hover:border-slate-400/50 hover:bg-white/60 transition-all group"
            >
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
                <LogIn className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display font-bold text-slate-900 text-base mb-1">Already registered?</h3>
              <p className="text-slate-500 text-xs leading-relaxed mb-4">
                Sign in to manage your products and share your catalog link.
              </p>
              <span className="text-xs font-bold text-slate-700 group-hover:underline flex items-center gap-1">
                Sign in to dashboard <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 text-center">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} VendorPulse · Built for Ghana micro-vendors ·{' '}
          <button onClick={() => onOpenAuth('register')} className="text-orange-500 hover:underline">
            Get started free
          </button>
        </p>
      </footer>
    </div>
  );
}
